import React, { ReactElement, useState } from 'react';

import { ButtonIcon } from '@coachhubio/nova-button';
import { Divider } from '@coachhubio/nova-divider';
import { Column, Row } from '@coachhubio/nova-flex';
import { ChevronDownIcon, ChevronRightIcon } from '@coachhubio/nova-icon';
import { Skeleton } from '@coachhubio/nova-skeleton';
import { Heading, Text } from '@coachhubio/nova-text';
import useGetLookerData from 'hooks/useGetLookerData';
import { useTranslation } from 'react-i18next';
import { InlinePlaceholder } from 'screens/Dashboard/components/Common';
import { LookerFieldsTag } from 'screens/Dashboard/utils/lookerFields';

import {
  textsColor,
  GreenBadge,
  BadgeSeparator,
  ColorBoxPre,
  ColorBoxPost,
  BarMarkerDifference,
  BarPostMarker,
  BarPreMarker,
  BarWrapper,
  ExpandedBadgeColumn,
  ExpandedBadgeContentRow,
  ExpandedBadgeFooterRow,
  ExpandedBadgeTitleRow,
  ExpandedLeftColumn,
  ExpandedRightColumn,
  ExpandedRow,
  ProgressGoalsTable,
  TableCellCenter,
  TableCellLeft,
  TableCellRight,
  TableRow,
  TableRowWithHover,
  TableWrapper,
  TableRulerMark,
  TableRulerWrapper,
} from '../ImpactPlus.styles';
import {
  ImpactProgressFocusAreaProgressData,
  ImpactProgressFocusAreaSelfAssessmentResultData,
  ImpactProgressFocusAreaDetailedResultsData,
  ImpactPlusRatingTableProps,
  FocusAreaDetailedResut,
  FocusAreaDetailsMap,
  FocusAreaRating,
  ImpactPlusProgressTableProps,
  ImpactProgressOverviewData,
  IPRatingBadgeProps,
  IPPercentageBadgeProps,
  ImpactProgressRelativeProgressFocusAreaData,
} from '../ImpactPlus.types';

function IPBadges() {
  const { t } = useTranslation('Dashboard');
  const { data: overviewArray, loading } = useGetLookerData<ImpactProgressOverviewData[]>(
    LookerFieldsTag.ImpactProgressOverview
  );
  const overviewElement = overviewArray?.[0];

  const { data: relativeProgressArray, loading: relativeProgressLoading } = useGetLookerData<
    ImpactProgressRelativeProgressFocusAreaData[]
  >(LookerFieldsTag.ImpactProgressRelativeProgressFocusArea);
  let maxFocusArea: ImpactProgressRelativeProgressFocusAreaData | undefined;
  let minFocusArea: ImpactProgressRelativeProgressFocusAreaData | undefined;

  if (relativeProgressLoading !== true) {
    maxFocusArea = relativeProgressArray?.find((fa) => fa['focus_area_max'] === 'Yes');
    minFocusArea = relativeProgressArray?.find((fa) => fa['focus_area_min_1'] === 'Yes');
  }

  function validNumber(value?: number) {
    if (value) {
      return Number(value);
    }
    return 0;
  }

  function IPRatingBadge(props: IPRatingBadgeProps) {
    return (
      <>
        <Column>
          <Row>
            <Text modifier="bold" size="xs" color={textsColor}>
              {props.title}
            </Text>
          </Row>
          <Row alignItems="baseline">
            <Text alignSelf='baseline' modifier="bold" size="xl" color={textsColor}>
              {props.value}
            </Text>
            <Text alignSelf='baseline' marginStart="s" size="xs" color="neutral80">
              /{props.base}
            </Text>
          </Row>
        </Column>
      </>
    );
  }

  function IPPercentageBadge(props: IPPercentageBadgeProps) {
    return (
      <>
        <Column alignItems="end">
          <Row alignItems="center">
            <Text modifier="bold" size="xs" color={textsColor}>
              {props.title}
            </Text>
          </Row>
          <Row alignItems="center">
            <Text modifier="bold" size="xl" color={textsColor}>
              {props.value < 0 ? '' : '+'} {props.value.toFixed(0)}%
            </Text>
          </Row>
          <Row>
            <Text size="xs" color="text40">
              {props.base}
            </Text>
          </Row>
        </Column>
      </>
    );
  }

  if (loading !== true && overviewElement) {
    const overviewDataObject = {
      prePoints: validNumber(overviewElement['coachhub_feedback_progress.avg_pre_assessment_result']),
      postPoints: validNumber(overviewElement['coachhub_feedback_progress.avg_post_assessment_result']),
      avgPercent: validNumber(
        overviewElement['avg_assessment_result_progress_percentage']
      ),
      highestPercent: validNumber(
        overviewElement['coachhub_feedback_progress.max_assessment_result_progress_percentage']
      ),
      lowestPercent: validNumber(
        overviewElement['coachhub_feedback_progress.min_assessment_result_progress_percentage']
      ),
    };

    return (
      <Column justifyContent="space-between">
        {loading ? (
          <InlinePlaceholder $height="100px" $width="100%">
            <Skeleton height="100px" width="100%" />
          </InlinePlaceholder>
        ) : (
          <Row justifyContent="space-between" marginX="s">
            <IPRatingBadge
              title={t('impactPlus.progress.averagePreProgramRating')}
              value={overviewDataObject.prePoints.toFixed(1)}
              base="10"
            />
            <IPRatingBadge
              title={t('impactPlus.progress.averagePostProgramRating')}
              value={overviewDataObject.postPoints.toFixed(1)}
              base="10"
            />
            <IPPercentageBadge
              title={t('impactPlus.progress.averageProgress')}
              value={overviewDataObject.avgPercent}
              base=""
            />
            {relativeProgressLoading ? (
              <InlinePlaceholder $height="100px" $width="40%">
                <Skeleton height="100px" width="100%" />
              </InlinePlaceholder>
            ) : (
              <>
                <IPPercentageBadge
                  title={t('impactPlus.progress.highestProgress')}
                  value={
                    maxFocusArea
                      ? maxFocusArea['avg_assessment_result_progress_percentage']
                      : 0
                  }
                  base={
                    maxFocusArea
                      ? t(maxFocusArea['coachhub_feedback_focus_areas.focus_area_name'].replaceAll('::', '.'))
                      : ''
                  }
                />
                <IPPercentageBadge
                  title={t('impactPlus.progress.lowestProgress')}
                  value={
                    minFocusArea
                      ? minFocusArea['avg_assessment_result_progress_percentage']
                      : 0
                  }
                  base={
                    minFocusArea
                      ? t(minFocusArea['coachhub_feedback_focus_areas.focus_area_name'].replaceAll('::', '.'))
                      : ''
                  }
                />
              </>
            )}
          </Row>
        )}
      </Column>
    );
  }
}

function TopLegend() {
  const { t } = useTranslation('Dashboard');

  return (
    <>
      <Row alignItems="center" justifyContent="start">
        <ColorBoxPre />
        &nbsp;
        <Text size="xs" color="neutral45">
          {t('impactPlus.progress.preProgram')}
        </Text>
        &nbsp;&nbsp;&nbsp;&nbsp;
        <ColorBoxPost />
        &nbsp;
        <Text size="xs" color="neutral45">
          {t('impactPlus.progress.postProgram')}
        </Text>
      </Row>
    </>
  );
}

function BodyExpandedBadge(props: { isPre: boolean; focusAreaDetails?: FocusAreaDetailedResut }) {
  const { t } = useTranslation('Dashboard');

  return (
    <ExpandedBadgeColumn>
      <ExpandedBadgeTitleRow justifyContent="center" alignItems="center" isPre={props.isPre}>
        <Text size="xs" modifier="bold">
          {props.isPre ? t('impactPlus.progress.preProgram') : t('impactPlus.progress.postProgram')}
        </Text>
      </ExpandedBadgeTitleRow>
      <ExpandedBadgeContentRow justifyContent="center">
        <Row justifyContent="end" style={{ width: '100%' }}>
          <Column marginX="m" alignItems="end" style={{ width: '45%' }}>
            <Row>
              <Text modifier="bold" color={textsColor} paddingY="xs" size="xs">
                {t('impactPlus.progress.selfRating')}
              </Text>
            </Row>
            <Row>
              <Column justifyContent="end">
                <Heading size="l" color={textsColor} paddingY="none" marginBottom="none">
                  {props.isPre
                    ? props.focusAreaDetails?.preSelf.toFixed(1)
                    : props.focusAreaDetails?.postSelf.toFixed(1)}
                </Heading>
              </Column>
              <Column justifyContent="end">
                <Text size="xs" color="neutral60">
                  /10
                </Text>
              </Column>
            </Row>
            {!props.isPre &&
              Number(props.focusAreaDetails?.postSelfProgressPoints) !== 0 &&
              Number(props.focusAreaDetails?.postSelfProgressPercentage) !== 0 &&
              props.focusAreaDetails?.postSelfProgressPoints &&
              props.focusAreaDetails?.postSelfProgressPercentage && (
                <GreenBadge isPositive={props.focusAreaDetails?.postSelfProgressPercentage >= 0}>
                  <Text size="xs" modifier="bold">
                    {props.focusAreaDetails?.postSelfProgressPoints > 0 ? '+' : ''}
                    {props.focusAreaDetails?.postSelfProgressPoints.toFixed(1)} pts.&nbsp;
                    {props.focusAreaDetails?.postSelfProgressPercentage > 0 ? '+' : ''}
                    {props.focusAreaDetails?.postSelfProgressPercentage.toFixed(0)}%&nbsp;
                    {props.focusAreaDetails?.postSelfProgressPercentage >= 0 ? '↑' : '↓'}
                  </Text>
                </GreenBadge>
              )}
          </Column>
          <Column style={{ width: '2px' }} justifyContent="center">
            <BadgeSeparator />
          </Column>
          <Column alignItems="start" marginX="m" style={{ width: '45%' }}>
            <Row>
              <Text modifier="bold" color={textsColor} paddingY="xs" size="xs">
                {t('impactPlus.progress.peerRating')}
              </Text>
            </Row>
            <Row>
              <Column justifyContent="end">
                <Heading size="l" color={textsColor}>
                  {props.isPre
                    ? props.focusAreaDetails?.prePeer.toFixed(1)
                    : props.focusAreaDetails?.postPeer.toFixed(1)}
                </Heading>
              </Column>
              <Column justifyContent="end">
                <Text size="xs" color="neutral60">
                  /10
                </Text>
              </Column>
            </Row>
            {!props.isPre &&
              Number(props.focusAreaDetails?.postPeerProgressPoints) !== 0 &&
              Number(props.focusAreaDetails?.postPeerProgressPercentage) !== 0 &&
              props.focusAreaDetails?.postPeerProgressPoints &&
              props.focusAreaDetails?.postPeerProgressPercentage && (
                <GreenBadge isPositive={props.focusAreaDetails?.postPeerProgressPercentage >= 0}>
                  <Text size="xs" modifier="bold">
                    {props.focusAreaDetails?.postPeerProgressPoints > 0 ? '+' : ''}
                    {props.focusAreaDetails?.postPeerProgressPoints.toFixed(1)} pts.&nbsp;
                    {props.focusAreaDetails?.postPeerProgressPercentage > 0 ? '+' : ''}
                    {props.focusAreaDetails?.postPeerProgressPercentage.toFixed(0)}%&nbsp;
                    {props.focusAreaDetails?.postPeerProgressPercentage >= 0 ? '↑' : '↓'}
                  </Text>
                </GreenBadge>
              )}
          </Column>
        </Row>
      </ExpandedBadgeContentRow>
      <ExpandedBadgeFooterRow justifyContent="center" alignItems="end">
        <Text size="xs" color="neutral45" modifier="regular">
          {props.isPre
            ? `Based on ${props.focusAreaDetails?.mCoacheesWPreSelfAssessment} coachee self-ratings and ${props.focusAreaDetails?.mPrePeerRespondents} peer-ratings`
            : `Based on ${props.focusAreaDetails?.mCoacheesWPostAssessment} coachee self-ratings and ${props.focusAreaDetails?.mPostPeerRespondents} peer-ratings`}
        </Text>
      </ExpandedBadgeFooterRow>
    </ExpandedBadgeColumn>
  );
}
function BodyExpandedRow(props: {
  focusArea: string;
  focusAreaDetails?: FocusAreaDetailedResut;
  rowExpandState: Record<string, boolean>;
  index: number;
}) {
  const isRowExpanded = props.rowExpandState[`${props.index}-row`] || false;

  return (
    <ExpandedRow isExpanded={isRowExpanded} justifyContent="end">
      <ExpandedLeftColumn>
        <BodyExpandedBadge isPre={true} focusAreaDetails={props.focusAreaDetails} />
      </ExpandedLeftColumn>
      <ExpandedRightColumn>
        <BodyExpandedBadge isPre={false} focusAreaDetails={props.focusAreaDetails} />
      </ExpandedRightColumn>
    </ExpandedRow>
  );
}

function TableHeader() {
  function TopRuler() {
    const rulerValues = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    return (
      <TableRulerWrapper>
        {rulerValues.map((value) => (
          <TableRulerMark key={`${value}-ruler`} left={value * 10}>
            <Text size="xs" color={textsColor}>
              {value}
            </Text>
          </TableRulerMark>
        ))}
      </TableRulerWrapper>
    );
  }

  return (
    <TableRow>
      <TableCellLeft></TableCellLeft>
      <TableCellCenter>
        <TopRuler />
      </TableCellCenter>
      <TableCellRight></TableCellRight>
    </TableRow>
  );
}
function TableBodyRow(props: {
  index: number;
  focusArea: string;
  pre: number;
  isPreEnabled: boolean;
  post: number;
  isPostEnabled: boolean;
  diffPrePostPts: string;
  diffPrePostPercent: string;
  isExpandable: boolean;
  rowExpandState?: Record<string, boolean>;
  setRowExpandState?: (state: Record<string, boolean>) => void;
}) {
  const isRowExpanded = props.rowExpandState?.[`${props.index}-row`] || false;

  return (
    <TableRowWithHover paddingY="xs" alignItems="center" key={`${props.index}-row`} isExpanded={isRowExpanded} 
    onClick={(e) => {
      e.stopPropagation();
      if (props.isExpandable && props.setRowExpandState) {
        props.setRowExpandState({
          ...props.rowExpandState,
          [`${props.index}-row`]: !isRowExpanded,
        });
      }
    }}>
      <TableCellLeft>
        <Row alignItems="center" justifyContent="space-between">
          <Column>
            {props.isExpandable && (
              <ButtonIcon
                size="xs"
                Icon={isRowExpanded ? ChevronDownIcon : ChevronRightIcon}
                onClick={(e) => {
                  e.stopPropagation();
                  if (props.setRowExpandState) {
                    props.setRowExpandState({
                      ...props.rowExpandState,
                      [`${props.index}-row`]: !isRowExpanded,
                    });
                  }
                }}
              />
            )}
          </Column>
          <Column>
            <Text
              style={{ textAlign: 'right' }}
              size="xs"
              color={textsColor}
              modifier={isRowExpanded ? 'bold' : 'regular'}
            >
              {props.focusArea}
            </Text>
          </Column>
        </Row>
      </TableCellLeft>
      <TableCellCenter>
        <BarWrapper>
          {props.isPreEnabled && props.isPostEnabled && (
            <BarMarkerDifference
              left={props.pre}
              right={props.post}
              isPositive={props.post >= props.pre}
            ></BarMarkerDifference>
          )}
          {props.isPreEnabled && (
            <BarPreMarker left={props.pre} isPositive={props.post >= props.pre}>
              <span>{props.pre.toFixed(1)}</span>
            </BarPreMarker>
          )}
          {props.isPostEnabled && (
            <BarPostMarker left={props.post} isPositive={props.post >= props.pre}>
              <span>{props.post.toFixed(1)}</span>
            </BarPostMarker>
          )}
        </BarWrapper>
      </TableCellCenter>
      <TableCellRight>
        {props.isPreEnabled && props.isPostEnabled && (
          <Row>
            <Column paddingEnd="none" style={{ width: '55px' }}>
              <Text size="xs" color={Number(props.diffPrePostPts) >= 0 ? 'success20' : 'error35'} modifier="bold">
                {Number(props.diffPrePostPts) < 0 ? '' : '+'}
              {props.diffPrePostPts} pts.
            </Text>
          </Column>
          <Column style={{ width: '8px' }}>
            <Text size="xs" color="neutral60">
              |
            </Text>
          </Column>
          <Column paddingStart="xs">
            <Text size="xs" color={Number(props.diffPrePostPercent) >= 0 ? 'success20' : 'error35'} modifier="bold">
              {Number(props.diffPrePostPercent) < 0 ? '' : '+'}
              {props.diffPrePostPercent}%
            </Text>
            </Column>
          </Row>
        )}
      </TableCellRight>
    </TableRowWithHover>
  );
}

function ImpactPlusProgressRatingTable({
  focusAreaRatings,
  title,
  isExpandable,
  focusAreaDetails,
}: ImpactPlusRatingTableProps): ReactElement {
  const [rowExpandState, setExpandState] = useState<Record<string, boolean>>({});
  const { t } = useTranslation('Dashboard');
  return (
    <>
      <Text modifier="bold" color={textsColor} paddingTop="3xl" paddingBottom="none" size="s">
        {title}
      </Text>
      <TableWrapper>
        <TableHeader />
      </TableWrapper>
      {focusAreaRatings?.map((faRow, index) => (
        <TableWrapper key={`${faRow.focusArea}-${index}-wrapper`}>
          <TableBodyRow
            index={index}
            key={`${faRow.focusArea}-${index}`}
            focusArea={`${t(faRow.focusArea.replaceAll('::', '.'))}`}
            pre={Number(faRow.pre)}
            isPreEnabled={faRow.isPreEnabled}
            post={Number(faRow.post)}
            isPostEnabled={faRow.isPostEnabled}
            diffPrePostPts={Number(faRow.diffPrePostPts).toFixed(1)}
            diffPrePostPercent={Number(faRow.diffPrePostPercent).toFixed(0)}
            isExpandable={isExpandable || false}
            rowExpandState={rowExpandState}
            setRowExpandState={setExpandState}
          />
          {isExpandable && (
            <BodyExpandedRow
              key={`${faRow.focusArea}-${index}-expanded-row`}
              focusArea={`${faRow.focusArea}`}
              focusAreaDetails={focusAreaDetails?.[faRow.focusArea]}
              rowExpandState={rowExpandState}
              index={index}
            />
          )}
        </TableWrapper>
      ))}
    </>
  );
}

function ImpactPlusProgressTable({ orgHasResults, orgHasSelfResults }: ImpactPlusProgressTableProps): ReactElement {
  const { t } = useTranslation('Dashboard');
  const { data: focusAreaArrayPeerAndSelf, loading: loadingPeerAndSelf } = useGetLookerData<
    ImpactProgressFocusAreaProgressData[]
  >(LookerFieldsTag.ImpactProgressFocusAreaProgress);
  const focusAreaPeerAndSelfRatings: FocusAreaRating[] =
    focusAreaArrayPeerAndSelf?.map((fa) => ({
      focusArea: fa['coachhub_feedback_focus_areas.focus_area_name'],
      isPreEnabled: fa['coachhub_feedback_progress.avg_pre_assessment_result'] !== null,
      isPostEnabled: fa['coachhub_feedback_progress.avg_post_assessment_result'] !== null,
      pre: Number(fa['coachhub_feedback_progress.avg_pre_assessment_result']),
      post: Number(fa['coachhub_feedback_progress.avg_post_assessment_result']),
      diffPrePostPts: fa['avg_assessment_result_progress'],
      diffPrePostPercent: fa['avg_assessment_result_progress_percentage'],
    })) || [];

  const { data: selfAssessmentFocusAreaArray, loading: loadingSelfAssessment } = useGetLookerData<
    ImpactProgressFocusAreaSelfAssessmentResultData[]
  >(LookerFieldsTag.ImpactProgressFocusAreaSelfAssessmentResult);
  const focusAreaSelfRatings: FocusAreaRating[] =
    selfAssessmentFocusAreaArray?.map((fa) => ({
      focusArea: fa['coachhub_feedback_focus_areas.focus_area_name'],
      isPreEnabled: fa['coachhub_feedback_progress.avg_pre_self_assessment_result'] !== null,
      isPostEnabled: fa['coachhub_feedback_progress.avg_post_self_assessment_result'] !== null,
      pre: Number(fa['coachhub_feedback_progress.avg_pre_self_assessment_result']),
      post: Number(fa['coachhub_feedback_progress.avg_post_self_assessment_result']),
      diffPrePostPts: fa['avg_self_assessment_progress'],
      diffPrePostPercent: fa['avg_self_assessment_progress_percentage'],
    })) || [];

  const { data: detailedResultsArray, loading: detailedResultsLoading } = useGetLookerData<
    ImpactProgressFocusAreaDetailedResultsData[]
  >(LookerFieldsTag.ImpactProgressFocusAreaDetailedResults);
  const focusAreaDetailedResults: FocusAreaDetailsMap = {};

  if (!detailedResultsLoading) {
    detailedResultsArray?.map(
      (fa) =>
        (focusAreaDetailedResults[fa['coachhub_feedback_focus_areas.focus_area_name']] = {
          focusArea: fa['coachhub_feedback_focus_areas.focus_area_name'],
          prePeer: Number(fa['coachhub_feedback_progress.avg_pre_peer_assessment_result']),
          preSelf: Number(fa['coachhub_feedback_progress.avg_pre_self_assessment_result']),
          postPeer: Number(fa['coachhub_feedback_progress.avg_post_peer_assessment_result']),
          postSelf: Number(fa['coachhub_feedback_progress.avg_post_self_assessment_result']),
          postSelfProgressPoints: Number(fa['avg_self_assessment_progress']),
          postSelfProgressPercentage: Number(
            fa['avg_self_assessment_progress_percentage']
          ),
          postPeerProgressPoints: Number(fa['avg_peer_assessment_progress']),
          postPeerProgressPercentage: Number(
            fa['avg_peer_assessment_progress_percentage']
          ),
          mCoacheesWPostAssessment: Number(fa['coachhub_feedback_progress.m_coachees_w_post_assessment']),
          mCoacheesWPreSelfAssessment: Number(fa['coachhub_feedback_progress.m_coachees_w_pre_self_assessment']),
          mPostPeerRespondents: Number(fa['coachhub_feedback_progress.m_post_peer_respondents']),
          mPrePeerRespondents: Number(fa['coachhub_feedback_progress.m_pre_peer_respondents']),
        })
    );
  }

  return (
    <ProgressGoalsTable>
      <IPBadges />
      <Divider marginY="m" marginX="none" color="neutral95" />
      <TopLegend />
      {loadingPeerAndSelf ? (
        <InlinePlaceholder $height="200px" $width="100%">
          <Skeleton height="200px" width="100%" />
        </InlinePlaceholder>
      ) : (
        orgHasResults && (
          <ImpactPlusProgressRatingTable
            isExpandable={true}
            title={t('impactPlus.progressTitle.peerAndSelfRating')}
            focusAreaRatings={focusAreaPeerAndSelfRatings || []}
            focusAreaDetails={focusAreaDetailedResults || {}}
          />
        )
      )}
      {loadingSelfAssessment ? (
        <InlinePlaceholder $height="200px" $width="100%">
          <Skeleton height="200px" width="100%" />
        </InlinePlaceholder>
      ) : (
        orgHasSelfResults && (
          <ImpactPlusProgressRatingTable
            isExpandable={false}
            title={t('impactPlus.progressTitle.selfRatingOnly')}
            focusAreaRatings={focusAreaSelfRatings || []}
          />
        )
      )}
    </ProgressGoalsTable>
  );
}

export default ImpactPlusProgressTable;
